'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChat } from "../hooks/useChat.js";
import { useChatStatus } from "../hooks/useChatStatus.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { jsx as _jsx } from "react/jsx-runtime";
function resolveTypingUser(userId, participants, messageAuthors) {
  return participants?.find(participant => participant.id === userId) ?? messageAuthors.find(author => author.id === userId) ?? {
    id: userId
  };
}
export const TypingIndicator = /*#__PURE__*/React.forwardRef(function TypingIndicator(props, ref) {
  const {
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    activeConversationId,
    conversations,
    messages
  } = useChat();
  const {
    typingUserIds
  } = useChatStatus();
  const localeText = useChatLocaleText();
  const conversation = React.useMemo(() => activeConversationId == null ? null : conversations.find(candidate => candidate.id === activeConversationId) ?? null, [activeConversationId, conversations]);
  const messageAuthors = React.useMemo(() => messages.reduce((authors, message) => {
    if (message.author && !authors.some(author => author.id === message.author.id)) {
      authors.push(message.author);
    }
    return authors;
  }, []), [messages]);
  const users = React.useMemo(() => typingUserIds.map(userId => resolveTypingUser(userId, conversation?.participants, messageAuthors)), [conversation?.participants, messageAuthors, typingUserIds]);
  const label = React.useMemo(() => users.length === 0 ? '' : localeText.typingIndicatorLabel(users), [localeText, users]);
  const ownerState = React.useMemo(() => ({
    activeConversationId,
    users,
    count: users.length,
    label
  }), [activeConversationId, label, users]);
  const Root = slots?.root ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref,
      'aria-live': 'polite'
    }, getDataAttributes({
      count: ownerState.count
    }))
  });
  if (users.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
    children: label
  }));
});
if (process.env.NODE_ENV !== "production") TypingIndicator.displayName = "TypingIndicator";